import { parseNumberInput } from "@/lib/parse-number";

/**
 * A multi-year grant budget with indirect (facilities and administrative, F&A) costs.
 *
 * The rules this follows are the federal Uniform Guidance, 2 CFR 200.1, as revised for
 * awards made on or after 1 October 2024:
 *
 * - **Modified total direct costs (MTDC)** are "all direct salaries and wages, applicable
 *   fringe benefits, materials and supplies, services, travel, and up to the first
 *   $50,000 of each subaward (regardless of the period of performance of the subawards
 *   under the award)". MTDC excludes equipment, capital expenditures, charges for patient
 *   care, rental costs, tuition remission, scholarships and fellowships, participant
 *   support costs, and the portion of each subaward above $50,000. (Before the revision the
 *   subaward figure was $25,000.)
 * - **Equipment** is tangible personal property with a useful life of more than one year
 *   and a per-unit cost that "equals or exceeds the lesser of the capitalization level
 *   established by the recipient … for financial statement purposes, or $10,000" (it was
 *   $5,000).
 * - **Total direct costs (TDC)** is the base some rates use instead: every direct cost.
 *
 * The indirect rate is always the user's: the tool takes no view on any institution's
 * negotiated rate. Everything is rounded to whole dollars line by line, as budget forms
 * ask, so the printed columns add up exactly.
 */

export const MAX_YEARS = 10;
export const DEFAULT_EQUIPMENT_THRESHOLD = 10_000;
export const DEFAULT_SUBAWARD_THRESHOLD = 50_000;

export type AmountCategory =
  | "travel"
  | "supplies"
  | "subawards"
  | "participants"
  | "excluded";

export type LineCategory = "personnel" | "equipment" | AmountCategory;

export interface PersonnelLine {
  label: string;
  /** Annual base salary for a full-time appointment, in dollars. */
  baseSalary: number;
  /** Effort as a percentage of that appointment, 0–100. */
  effort: number;
  /** This person's fringe rate in percent; null uses the default rate. */
  fringe: number | null;
}

export interface AmountLine {
  label: string;
  amount: number;
  fromYear: number;
  toYear: number;
}

export interface EquipmentLine {
  label: string;
  cost: number;
  fromYear: number;
  toYear: number;
}

export interface SkippedLine {
  line: number;
  text: string;
}

export interface Parsed<T> {
  lines: T[];
  skipped: SkippedLine[];
}

const LINE_BREAK = /\r\n|\r|\n/;
const PERCENT_SIGN = /%/g;
const YEAR_SPEC = /^(\d{1,2})(?:\s*[-–—]\s*(\d{1,2}))?$/;
const CURRENCY = /[$€£\s]/g;

/** Tab, then pipe, then semicolon, then comma — the first one the line contains. */
export function splitFields(line: string): string[] {
  for (const delimiter of ["\t", "|", ";", ","]) {
    if (line.includes(delimiter)) {
      return line.split(delimiter).map((field) => field.trim());
    }
  }
  return [line.trim()];
}

function amountOf(field: string | undefined): number | null {
  if (field === undefined) {
    return null;
  }
  const value = parseNumberInput(field.replace(CURRENCY, ""));
  return value !== null && value >= 0 ? value : null;
}

/** "2" → years 2–2; "1-3" → 1–3; blank → every year. Null when it cannot be read. */
export function parseYears(
  field: string | undefined,
  years: number
): [number, number] | null {
  if (field === undefined || field.trim() === "") {
    return [1, years];
  }
  const match = YEAR_SPEC.exec(field.trim());
  if (!match) {
    return null;
  }
  const from = Number(match[1]);
  const to = match[2] === undefined ? from : Number(match[2]);
  return from >= 1 && to >= from ? [from, to] : null;
}

function eachLine(
  text: string,
  read: (fields: string[]) => boolean
): SkippedLine[] {
  const skipped: SkippedLine[] = [];
  const rows = text.split(LINE_BREAK);
  rows.forEach((row, index) => {
    if (row.trim() === "") {
      return;
    }
    if (!read(splitFields(row))) {
      skipped.push({ line: index + 1, text: row.trim() });
    }
  });
  return skipped;
}

/** `Role, annual base salary, effort %, fringe % (optional)`. */
export function parsePersonnel(text: string): Parsed<PersonnelLine> {
  const lines: PersonnelLine[] = [];
  const skipped = eachLine(text, ([label, salary, effort, fringe]) => {
    const baseSalary = amountOf(salary);
    const effortValue = amountOf(effort?.replace(PERCENT_SIGN, ""));
    const fringeValue =
      fringe === undefined || fringe === ""
        ? null
        : amountOf(fringe.replace(PERCENT_SIGN, ""));
    const fringeBad =
      fringe !== undefined && fringe !== "" && fringeValue === null;
    if (
      !label ||
      baseSalary === null ||
      effortValue === null ||
      effortValue > 100 ||
      fringeBad
    ) {
      return false;
    }
    lines.push({
      label,
      baseSalary,
      effort: effortValue,
      fringe: fringeValue,
    });
    return true;
  });
  return { lines, skipped };
}

/** `Description, amount per year, years (optional: 2 or 1-3)`. */
export function parseAmounts(text: string, years: number): Parsed<AmountLine> {
  const lines: AmountLine[] = [];
  const skipped = eachLine(text, ([label, amount, span]) => {
    const value = amountOf(amount);
    const range = parseYears(span, years);
    if (!label || value === null || range === null) {
      return false;
    }
    lines.push({ label, amount: value, fromYear: range[0], toYear: range[1] });
    return true;
  });
  return { lines, skipped };
}

/** `Item, cost, year (optional: 2 or 1-3 — one purchase in each year named)`. */
export function parseEquipment(
  text: string,
  years: number
): Parsed<EquipmentLine> {
  const parsed = parseAmounts(text, years);
  return {
    lines: parsed.lines.map((line) => ({
      label: line.label,
      cost: line.amount,
      fromYear: line.fromYear,
      toYear: line.toYear,
    })),
    skipped: parsed.skipped,
  };
}

/* ------------------------------------------------------------------ the calculation */

export type BaseKind = "mtdc" | "tdc";

export interface BudgetInput {
  years: number;
  /** Annual raise applied to salaries from year 2, in percent. */
  salaryEscalation: number;
  /** Annual increase applied to travel, supplies, participant support and excluded costs. */
  otherEscalation: number;
  /** Default fringe rate in percent. */
  fringe: number;
  equipmentThreshold: number;
  subawardThreshold: number;
  base: BaseKind;
  /** The indirect rate in percent. */
  indirectRate: number;
  personnel: readonly PersonnelLine[];
  equipment: readonly EquipmentLine[];
  travel: readonly AmountLine[];
  supplies: readonly AmountLine[];
  subawards: readonly AmountLine[];
  participants: readonly AmountLine[];
  excluded: readonly AmountLine[];
}

export interface YearBudget {
  /** 1-based; 0 for the total column. */
  year: number;
  salaries: number;
  fringe: number;
  equipment: number;
  travel: number;
  supplies: number;
  subawards: number;
  participants: number;
  excluded: number;
  direct: number;
  /** Part of the subawards inside the MTDC base (the first $50,000 of each). */
  subawardsInBase: number;
  base: number;
  indirect: number;
  total: number;
}

export interface SubawardExcess {
  label: string;
  /** Dollars over the threshold across the whole award. */
  excess: number;
}

export interface BudgetResult {
  years: YearBudget[];
  totals: YearBudget;
  /** Equipment lines under the threshold, counted as supplies. */
  reclassified: string[];
  subawardExcess: SubawardExcess[];
}

/** Whole dollars, with a hair added so 15,913.4999999 from compounding rounds as 15,913.5. */
export function dollars(value: number): number {
  return Math.round(value + 1e-9);
}

function inYears(line: { fromYear: number; toYear: number }, year: number) {
  return year >= line.fromYear && year <= line.toYear;
}

function factor(percent: number, year: number): number {
  return (1 + percent / 100) ** (year - 1);
}

function sumAmounts(
  lines: readonly AmountLine[],
  year: number,
  escalation: number
): number {
  let total = 0;
  for (const line of lines) {
    if (inYears(line, year)) {
      total += dollars(line.amount * factor(escalation, year));
    }
  }
  return total;
}

function subawardsFor(
  lines: readonly AmountLine[],
  years: number,
  threshold: number
) {
  const spent = new Map<string, number>();
  const excess = new Map<string, number>();
  const perYear: { total: number; inBase: number }[] = [];
  for (let year = 1; year <= years; year++) {
    let total = 0;
    let inBase = 0;
    for (const line of lines) {
      if (!inYears(line, year)) {
        continue;
      }
      const key = line.label.trim().toLowerCase();
      const before = spent.get(key) ?? 0;
      const amount = dollars(line.amount);
      const included = Math.max(0, Math.min(amount, threshold - before));
      spent.set(key, before + amount);
      excess.set(key, (excess.get(key) ?? 0) + (amount - included));
      total += amount;
      inBase += included;
    }
    perYear.push({ total, inBase });
  }
  const labels = new Map(
    lines.map((line) => [line.label.trim().toLowerCase(), line.label])
  );
  const over: SubawardExcess[] = [];
  for (const [key, value] of excess) {
    if (value > 0) {
      over.push({ label: labels.get(key) ?? key, excess: value });
    }
  }
  return { perYear, over };
}

function personnelFor(
  input: BudgetInput,
  year: number
): { salaries: number; fringe: number } {
  let salaries = 0;
  let fringe = 0;
  for (const person of input.personnel) {
    const salary = dollars(
      person.baseSalary *
        (person.effort / 100) *
        factor(input.salaryEscalation, year)
    );
    salaries += salary;
    fringe += dollars((salary * (person.fringe ?? input.fringe)) / 100);
  }
  return { salaries, fringe };
}

const SUM_KEYS = [
  "salaries",
  "fringe",
  "equipment",
  "travel",
  "supplies",
  "subawards",
  "participants",
  "excluded",
  "direct",
  "subawardsInBase",
  "base",
  "indirect",
  "total",
] as const;

export function computeBudget(input: BudgetInput): BudgetResult {
  const years = Math.min(MAX_YEARS, Math.max(1, Math.trunc(input.years)));
  const reclassified = new Set<string>();
  const subs = subawardsFor(input.subawards, years, input.subawardThreshold);
  const rows: YearBudget[] = [];
  for (let year = 1; year <= years; year++) {
    const { salaries, fringe } = personnelFor(input, year);
    let equipment = 0;
    let smallEquipment = 0;
    for (const item of input.equipment) {
      if (!inYears(item, year)) {
        continue;
      }
      const cost = dollars(item.cost);
      if (cost >= input.equipmentThreshold) {
        equipment += cost;
      } else {
        smallEquipment += cost;
        reclassified.add(item.label);
      }
    }
    const travel = sumAmounts(input.travel, year, input.otherEscalation);
    const supplies =
      sumAmounts(input.supplies, year, input.otherEscalation) + smallEquipment;
    const participants = sumAmounts(
      input.participants,
      year,
      input.otherEscalation
    );
    const excluded = sumAmounts(input.excluded, year, input.otherEscalation);
    const { total: subawards, inBase: subawardsInBase } =
      subs.perYear[year - 1];
    const direct =
      salaries +
      fringe +
      equipment +
      travel +
      supplies +
      subawards +
      participants +
      excluded;
    const base =
      input.base === "tdc"
        ? direct
        : salaries + fringe + travel + supplies + subawardsInBase;
    const indirect = dollars((base * input.indirectRate) / 100);
    rows.push({
      year,
      salaries,
      fringe,
      equipment,
      travel,
      supplies,
      subawards,
      participants,
      excluded,
      direct,
      subawardsInBase,
      base,
      indirect,
      total: direct + indirect,
    });
  }
  const totals = { year: 0 } as YearBudget;
  for (const key of SUM_KEYS) {
    totals[key] = rows.reduce((sum, row) => sum + row[key], 0);
  }
  return {
    years: rows,
    totals,
    reclassified: [...reclassified],
    subawardExcess: subs.over,
  };
}
